/**
 * CalculationContent — orchestrátor záložky „Calculation“ (layout B). Drží editovatelný stav tarifů (sdílené
 * schéma série + per-termín vlastní override), počítá metriky živě ({@link computeTermMetrics}) a skládá souhrn,
 * seznam termínů s inline editorem tarifů, PAX rozklad a spodní akční lištu (Uložit / Uložit a schválit).
 * Ukládání jde přes generované mutace; data se čtou jen přes generované hooky (žádný ruční fetch).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { GetCalculationResponse, SerieLayerStateItem } from '@/api/generated/schemas';
import { LayerState, SerieLayer } from '@/api/generated/schemas';
import {
  getGetVenuaPlannerSerieCalculationQueryKey,
  getGetVenuaPlannerSerieDetailQueryKey,
  getGetVenuaPlannerSerieEconomicsQueryKey,
  getGetVenuaPlannerTermCalculationQueryKey,
  useSaveVenuaPlannerSerieCalculationTariff,
  useSaveVenuaPlannerTermCalculationTariff,
} from '@/api/generated/venua-planner-planning/venua-planner-planning';
import { FormActionBar, toast } from '@/shared/ui';
import { venuaPlannerQueryKeys } from '../../../venuaPlannerApi';
import { ApproveLayerButton } from '../ApproveLayerButton';
import { useChangeSerieLayerState, useChangeTermLayerState } from '../useChangeLayerState';
import { CalculationSummaryBar } from './components/CalculationSummaryBar';
import { CalculationTermsTable } from './components/CalculationTermsTable';
import { PaxBreakdownRail } from './components/PaxBreakdownRail';
import { aggregateMetrics, computeTermMetrics, type TierDraft } from './model/calc';

/** Rozbalený (a tím vybraný) termín — řídí ho URL stav detailu, aby ho obnovil reload i deep-link. */
interface ExpandedTermControl {
  expandedTermId: string | null;
  onExpandedTermIdChange: (termId: string | null) => void;
}

type CalculationContentProps = ExpandedTermControl &
  (
    | {
        scope: 'serie';
        productId: string;
        serieId: string;
        termId?: undefined;
        /** Vrstvy seriálu – schvalovací tlačítko v patičce boardu. */
        layers: SerieLayerStateItem[];
        serieLayers?: undefined;
        onBack: () => void;
        response: GetCalculationResponse;
      }
    | {
        scope: 'term';
        productId: string;
        serieId: string;
        termId: string;
        /** Vrstvy termínu. */
        layers: SerieLayerStateItem[];
        /** Vrstvy seriálu – zdroj nabídky „schválit až po X“. */
        serieLayers: SerieLayerStateItem[];
        onBack: () => void;
        response: GetCalculationResponse;
      }
  );

type OwnMap = Record<string, TierDraft[] | null>;

function cloneTiers(tiers: { count: number; price: number }[]): TierDraft[] {
  return tiers.map((tier) => ({ count: tier.count, price: tier.price }));
}

function buildOwnMap(response: GetCalculationResponse): OwnMap {
  const map: OwnMap = {};
  for (const term of response.terms) {
    map[term.termGroupId] = term.isOwnTariff ? cloneTiers(term.tiers) : null;
  }
  return map;
}

export function CalculationContent(props: CalculationContentProps) {
  const {
    scope,
    productId,
    serieId,
    layers,
    serieLayers,
    onBack,
    response,
    expandedTermId,
    onExpandedTermIdChange,
  } = props;
  const { t } = useTranslation('venua-planner');
  const queryClient = useQueryClient();

  const [shared, setShared] = useState<TierDraft[]>(() => cloneTiers(response.sharedTiers));
  const [owns, setOwns] = useState<OwnMap>(() => buildOwnMap(response));
  const [sharedDirty, setSharedDirty] = useState(false);
  const [dirtyTerms, setDirtyTerms] = useState<Set<string>>(() => new Set());

  const [paxOpen, setPaxOpen] = useState(false);

  // Po (re)načtení dat ze serveru převezmi hodnoty a zahoď rozpracované změny (výběr/rozbalení zůstává).
  useEffect(() => {
    setShared(cloneTiers(response.sharedTiers));
    setOwns(buildOwnMap(response));
    setSharedDirty(false);
    setDirtyTerms(new Set());
  }, [response]);

  const schemeOf = useCallback(
    (termId: string): 'shared' | 'own' => (owns[termId] != null ? 'own' : 'shared'),
    [owns]
  );
  const tiersOf = useCallback(
    (termId: string): TierDraft[] => owns[termId] ?? shared,
    [owns, shared]
  );

  const metricsByTerm = useMemo(() => {
    const map = new Map<string, ReturnType<typeof computeTermMetrics>>();
    for (const term of response.terms) {
      const tiers = owns[term.termGroupId] ?? shared;
      map.set(term.termGroupId, computeTermMetrics(term, tiers, response.tariffRateToDomestic));
    }
    return map;
  }, [response.terms, response.tariffRateToDomestic, owns, shared]);

  const aggregate = useMemo(
    () => aggregateMetrics(response.terms, metricsByTerm),
    [response.terms, metricsByTerm]
  );

  // Souhrn i PAX rozklad ukazují rozbalený termín — rozbalení a výběr jsou jedno a totéž.
  const selected = useMemo(() => {
    if (!expandedTermId) return null;
    const term = response.terms.find((item) => item.termGroupId === expandedTermId);
    const metrics = metricsByTerm.get(expandedTermId);
    return term && metrics ? { term, metrics } : null;
  }, [expandedTermId, response.terms, metricsByTerm]);

  const markTermDirty = useCallback((termId: string) => {
    setDirtyTerms((prev) => {
      const next = new Set(prev);
      next.add(termId);
      return next;
    });
  }, []);

  // --- editace pásem ---
  const updateTiers = useCallback(
    (termId: string, updater: (tiers: TierDraft[]) => TierDraft[]) => {
      if (schemeOf(termId) === 'own') {
        setOwns((prev) => ({ ...prev, [termId]: updater(prev[termId] ?? cloneTiers(shared)) }));
        markTermDirty(termId);
      } else {
        setShared((prev) => updater(prev));
        setSharedDirty(true);
      }
    },
    [schemeOf, shared, markTermDirty]
  );

  const onTierChange = useCallback(
    (termId: string, index: number, patch: Partial<TierDraft>) =>
      updateTiers(termId, (tiers) =>
        tiers.map((tier, i) => (i === index ? { ...tier, ...patch } : tier))
      ),
    [updateTiers]
  );

  const onAddTier = useCallback(
    (termId: string) =>
      updateTiers(termId, (tiers) => {
        const last = tiers[tiers.length - 1] ?? { count: 0, price: 0 };
        const next = tiers.slice();
        next.splice(next.length - 1, 0, { count: 6, price: last.price });
        return next;
      }),
    [updateTiers]
  );

  const onRemoveTier = useCallback(
    (termId: string, index: number) =>
      updateTiers(termId, (tiers) =>
        tiers.length <= 1 ? tiers : tiers.filter((_, i) => i !== index)
      ),
    [updateTiers]
  );

  const onSchemeChange = useCallback(
    (termId: string, next: 'shared' | 'own') => {
      setOwns((prev) => ({
        ...prev,
        [termId]: next === 'own' ? (prev[termId] ?? cloneTiers(shared)) : null,
      }));
      markTermDirty(termId);
      onExpandedTermIdChange(termId);
    },
    [shared, markTermDirty, onExpandedTermIdChange]
  );

  const onToggleTerm = useCallback(
    (termId: string) => onExpandedTermIdChange(expandedTermId === termId ? null : termId),
    [expandedTermId, onExpandedTermIdChange]
  );

  // --- ukládání ---
  const saveSerie = useSaveVenuaPlannerSerieCalculationTariff();
  const saveTerm = useSaveVenuaPlannerTermCalculationTariff();
  // Oba hooky se volají bezpodmínečně (pravidla hooků); použije se jen ten dle scope.
  const serieChange = useChangeSerieLayerState({ productId, serieId });
  const termChange = useChangeTermLayerState({ productId, serieId, termId: props.termId ?? '' });
  const activeChangeState = scope === 'serie' ? serieChange.changeState : termChange.changeState;
  const activeChanging = scope === 'serie' ? serieChange.isPending : termChange.isPending;

  const saving = saveSerie.isPending || saveTerm.isPending || activeChanging;
  const dirty = sharedDirty || dirtyTerms.size > 0;

  // Tarifní tabulka je read-only, jakmile je vrstva Calculation připravená / schválená (jen změna stavu).
  const calculationState = layers.find((entry) => entry.layer === SerieLayer.Calculation)?.state;
  const layerReadOnly =
    calculationState === LayerState.Prepared || calculationState === LayerState.Approved;

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: getGetVenuaPlannerSerieCalculationQueryKey(productId, serieId),
    });
    if (scope === 'term') {
      void queryClient.invalidateQueries({
        queryKey: getGetVenuaPlannerTermCalculationQueryKey(productId, serieId, props.termId),
      });
    }
    void queryClient.invalidateQueries({
      queryKey: getGetVenuaPlannerSerieEconomicsQueryKey(productId, serieId),
    });
    void queryClient.invalidateQueries({
      queryKey: getGetVenuaPlannerSerieDetailQueryKey(productId, serieId),
    });
    void queryClient.invalidateQueries({ queryKey: venuaPlannerQueryKeys.tree() });
  }, [queryClient, scope, productId, serieId, props.termId]);

  const persist = useCallback(async (): Promise<boolean> => {
    try {
      if (sharedDirty) {
        await saveSerie.mutateAsync({
          productId,
          serieId,
          data: { useOwnScheme: null, tariffCurrency: response.tariffCurrency, tiers: shared },
        });
      }
      for (const termId of dirtyTerms) {
        const own = owns[termId];
        await saveTerm.mutateAsync({
          productId,
          serieId,
          termId,
          data: { useOwnScheme: own != null, tariffCurrency: null, tiers: own ?? [] },
        });
      }
      invalidate();
      toast({ severity: 'success', description: t('calculation.toast.saved') });
      return true;
    } catch {
      toast({
        severity: 'error',
        title: t('calculation.toast.errorTitle'),
        description: t('calculation.toast.saveError'),
      });
      return false;
    }
  }, [
    sharedDirty,
    dirtyTerms,
    owns,
    shared,
    response.tariffCurrency,
    productId,
    serieId,
    saveSerie,
    saveTerm,
    invalidate,
    t,
  ]);

  // Uložení rozpracovaných tarifů před akcí – `ApproveLayerButton` ho volá přes `onBeforeAction`.
  const persistBeforeApprove = useCallback(async () => {
    if (!dirty) return true;
    return persist();
  }, [dirty, persist]);

  // Změna stavu vrstvy Calculation (Připravit / Schválit / Vrátit / Vrátit schválení) přes sjednocený
  // endpoint. Toasty i sdílené invalidace řeší hook; navíc přepočítáme kalkulaci + ekonomiku série.
  const changeState = useCallback(
    async (layer: SerieLayer, targetState: LayerState): Promise<boolean> => {
      const ok = await activeChangeState(layer, targetState);
      invalidate();
      return ok;
    },
    [activeChangeState, invalidate]
  );

  return (
    <div className="bg-background flex h-full min-h-0 flex-col">
      <div className="p-phx-5 min-h-0 flex-1 overflow-hidden">
        <div className="gap-phx-3 flex h-full min-h-0">
          <div className="gap-phx-3 flex min-w-0 flex-1 flex-col overflow-auto">
            <CalculationSummaryBar selected={selected} aggregate={aggregate} />
            <CalculationTermsTable
              terms={response.terms}
              metricsByTerm={metricsByTerm}
              expandedTermId={expandedTermId}
              onToggleTerm={onToggleTerm}
              tariffCurrency={response.tariffCurrency}
              schemeOf={schemeOf}
              tiersOf={tiersOf}
              onSchemeChange={onSchemeChange}
              onTierChange={onTierChange}
              onAddTier={onAddTier}
              onRemoveTier={onRemoveTier}
              disabled={saving || layerReadOnly}
            />
          </div>
          <PaxBreakdownRail
            selected={selected}
            open={paxOpen}
            onToggle={() => setPaxOpen((prev) => !prev)}
          />
        </div>
      </div>

      <FormActionBar
        onBack={onBack}
        onSave={persist}
        saving={saving}
        disabled={!dirty}
        extraActions={
          <ApproveLayerButton
            currentLayer={SerieLayer.Calculation}
            layers={layers}
            serieLayers={serieLayers}
            onBeforeAction={persistBeforeApprove}
            onChangeState={changeState}
            disabled={saving}
            loading={activeChanging}
          />
        }
      />
    </div>
  );
}
